// /home/szymon/kreator2/frontend/src/components/editor/hooks/useEditorZoom.js
// v1.0.1 – Poprawka zachowania zoomu przy edycji

import { useState, useEffect, useCallback, useMemo } from "react";

export function useEditorZoom({ pages, currentPage }) {
  const [zoomFactors, setZoomFactors] = useState([]);

  const computeAutoZoom = useCallback((pageWidth, pageHeight) => {
    const LEFT_TOOLBAR_WIDTH = 70;
    const SIDE_PANEL_WIDTH = 300;
    const TOPBAR_HEIGHT = 60;
    const EXTRA_MARGIN = 80;
    const availableW =
      window.innerWidth - LEFT_TOOLBAR_WIDTH - SIDE_PANEL_WIDTH - EXTRA_MARGIN;
    const availableH = window.innerHeight - TOPBAR_HEIGHT - EXTRA_MARGIN;

    if (pageWidth <= 0 || pageHeight <= 0 || availableW < 10 || availableH < 10) {
      return 0.1; // Default to a small zoom if dimensions are invalid
    }
    const scale = Math.min(availableW / pageWidth, availableH / pageHeight);
    return Math.max(scale, 0.1); // Ensure zoom is not less than 10%
  }, []); // Empty dependency array as it only uses window global and constants

  const pageStructureSignature = useMemo(() => {
    if (!pages || pages.length === 0) return "no_pages";
    // Create a signature based on page count and their base dimensions
    return pages.map((p, index) => `page${index}_${p.baseWidth}x${p.baseHeight}`).join('|');
  }, [pages]);

  // Effect to initialize/update zoom factors ONLY when page structure changes
  useEffect(() => {
    if (!pages || pages.length === 0) {
      setZoomFactors([]);
      return;
    }

    setZoomFactors(prevZoomFactors => {
      const newZooms = pages.map((pg, index) => {
        // If a zoom factor for this page index already exists (and is a valid number),
        // and the number of pages hasn't changed significantly (e.g. page dimensions are the same),
        // preserve it. This means a user-set zoom will be kept.
        if (prevZoomFactors && prevZoomFactors.length === pages.length &&
            typeof prevZoomFactors[index] === 'number' && !isNaN(prevZoomFactors[index])) {
          // Additionally, check if page dimensions changed, which might warrant re-auto-zooming
          // This part is tricky because `pageStructureSignature` already covers baseWidth/baseHeight changes.
          // The main idea is to preserve user-set zoom if the page structure (count, dimensions) is stable.
          return prevZoomFactors[index];
        }
        // Otherwise (new page, page count changed, dimensions changed, or no previous zoom for this index),
        // calculate the auto-zoom.
        return computeAutoZoom(pg.baseWidth, pg.baseHeight);
      });
      return newZooms;
    });
  }, [pageStructureSignature, pages, computeAutoZoom]); // `pages` is needed for `pages.map`

  const currentZoom = useMemo(() => {
    if (!pages || pages.length === 0 || !zoomFactors || zoomFactors.length === 0) return 1;
    
    const safeCurrentPage = Math.max(0, Math.min(currentPage, pages.length - 1));

    if (safeCurrentPage >= zoomFactors.length || zoomFactors[safeCurrentPage] === undefined || isNaN(zoomFactors[safeCurrentPage])) {
      const pageData = pages[safeCurrentPage] || pages[0];
      return pageData ? computeAutoZoom(pageData.baseWidth, pageData.baseHeight) : 1;
    }
    return zoomFactors[safeCurrentPage];
  }, [pages, zoomFactors, currentPage, computeAutoZoom]);

  // Handler for when the user changes the zoom manually
  const handleZoomChange = useCallback(
    (newVal) => {
      if (isNaN(newVal) || newVal < 0.1 || newVal > 10) { // Basic validation
        console.warn(`[useEditorZoom] Invalid zoom value: ${newVal}`);
        return;
      }
      setZoomFactors((prev) => {
        const arr = [...prev];
        // Ensure currentPage is a valid index for the array
        if (currentPage >= 0 && currentPage < (pages?.length || 0)) {
           // If arr is shorter than pages (e.g. pages just loaded), fill up to currentPage
          while (arr.length <= currentPage) {
            const pageData = pages[arr.length];
            arr.push(pageData ? computeAutoZoom(pageData.baseWidth, pageData.baseHeight) : 1);
          }
          arr[currentPage] = newVal;
        } else {
          console.warn(`[useEditorZoom] handleZoomChange: currentPage index ${currentPage} is out of bounds for pages array (length ${pages?.length || 0}).`);
        }
        return arr;
      });
    },
    [currentPage, pages, computeAutoZoom] // Added pages and computeAutoZoom as dependencies
  );

  return {
    currentZoom,
    handleZoomChange
  };
}
